// Helpers that build TipTap-rich BriefDocument values from Templates and
// Blueprints so they can be pushed into the Draft Canvas as a clean starter.
//
// Routing rules (locked in mem://index.md):
//   - Templates and Blueprints push INTO the Draft Canvas (these helpers).
//   - Prompts push INTO the AI Chat (plain text, not here).

import type { BriefDocument, BlockNode, ParagraphNode, HeadingNode } from "@/lib/briefDocument";
import {
  blueprintToPlainText,
  resolveBlueprintBlockRef,
  type ComposerBlock,
  type ComposerBlueprint,
} from "@/lib/composerBlocks";
import { parseStructure, type WizardQuestion } from "@/lib/composerTemplates";

const para = (text?: string): ParagraphNode =>
  text && text.length > 0
    ? { type: "paragraph", content: [{ type: "text", text }] }
    : { type: "paragraph" };

const h2 = (text: string): HeadingNode => ({
  type: "heading",
  attrs: { level: 2 },
  content: [{ type: "text", text }],
});

const italicPara = (text: string): ParagraphNode => ({
  type: "paragraph",
  content: [{ type: "text", text, marks: [{ type: "italic" }] }],
});

/**
 * Build a starter BriefDocument from a template's intro message + structure.
 * Each section becomes an H2 heading + an empty paragraph for the user to fill in.
 */
export function templateToBriefDocument(args: {
  introMessage?: string | null;
  defaultStructure?: unknown;
}): BriefDocument {
  const sections = parseStructure(args.defaultStructure as never);
  const content: BlockNode[] = [];

  if (args.introMessage && args.introMessage.trim()) {
    content.push(italicPara(args.introMessage.trim()));
  }

  if (sections.length === 0) {
    content.push(para());
    return { type: "doc", content };
  }

  for (const s of sections) {
    content.push(h2(s.section));
    const hint: string[] = [];
    if (s.angle) hint.push(`Angle: ${s.angle}`);
    if (s.goal) hint.push(`Goal: ${s.goal}`);
    if (hint.length) content.push(italicPara(hint.join(" · ")));
    content.push(para());
  }

  return { type: "doc", content };
}

/**
 * Build a starter BriefDocument from a blueprint by expanding each block ref.
 * - Resolved blocks become paragraphs (one block per paragraph block).
 * - `quote`-typed blocks become pullQuote nodes.
 * - Unresolved refs become italic placeholder paragraphs so the user sees what to fill in.
 */
export function blueprintToBriefDocument(
  blueprint: ComposerBlueprint,
  blocks: ComposerBlock[],
): BriefDocument {
  const content: BlockNode[] = [];
  for (const ref of blueprint.blockRefs) {
    const block = resolveBlueprintBlockRef(ref, blocks);
    if (!block) {
      const note = ref.note ? ` — ${ref.note}` : "";
      content.push(italicPara(`[${ref.type}${note}]`));
      continue;
    }
    if (block.type === "quote") {
      // PullQuote node removed; render as a plain blockquote instead.
      content.push({
        type: "blockquote",
        content: [para(block.body)],
      });
      continue;
    }
    // Split the block body into paragraphs on blank lines.
    const paragraphs = block.body.split(/\n{2,}/g).map((p) => p.trim()).filter(Boolean);
    if (paragraphs.length === 0) {
      content.push(para());
    } else {
      for (const p of paragraphs) content.push(para(p));
    }
  }
  if (content.length === 0) content.push(para());
  return { type: "doc", content };
}


/** Re-export for convenience. */
export type { WizardQuestion };
